import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery as useQueryApollo,
} from '@apollo/client';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { DocumentNode } from 'graphql';
import { get } from 'lodash';
import { useCallback, useMemo } from 'react';

type ExtendedOptions = {
  key?: string;
  transform?: any;
  selector?: string;
};

/**
 * Checks whether the raw Apollo response data contains a datasource
 * envelope where success is false. Datasource responses have the shape:
 * { queryXxx: { id, type, success, result, error } }
 */
function hasDatasourceFailure(data: any): boolean {
  if (!data || typeof data !== 'object') return false;
  return Object.values(data).some(
    (value: any) =>
      value &&
      typeof value === 'object' &&
      'success' in value &&
      value.success === false
  );
}

export function useQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables> & ExtendedOptions
): QueryResult<TData, TVariables> & { dataUnavailable?: boolean } {
  const { key, transform, selector, ...options2 } = options || {};

  const selectAndTransformDataBlock = useCallback(
    (data: TData | undefined, options: ExtendedOptions) => {
      const { transform, selector } = options;

      if (selector && data) {
        data = get(data, selector);
      }

      if (data && typeof transform === 'function') {
        data = transform(data, options);
      }

      return data;
    },
    []
  );

  const queryResult = useQueryApollo(query, options2);

  const data = useMemo<TData | undefined>(
    () => {
      return selectAndTransformDataBlock(queryResult.data, {
        key,
        transform,
        selector,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryResult.data, selectAndTransformDataBlock, selector]
  );

  const dataUnavailable = useMemo(() => {
    if (!queryResult.data || queryResult.loading) return false;
    return hasDatasourceFailure(queryResult.data);
  }, [queryResult.data, queryResult.loading]);

  return useMemo(
    () => ({ ...queryResult, key, data, dataUnavailable }),
    [data, dataUnavailable, key, queryResult]
  );
}
